// src/features/team/TeamPanel.tsx
import { useState } from 'react';
import { calculateDamage } from '../../core/damage';
import type { Skill, DamageResult } from '../../core/types';
import type { SkillResultItem } from '../../store/useScenarioStore';
import { StatBlock } from '../../components/StatBlock';

import { ArrowLeftRight } from 'lucide-react';
import { useTeamStore } from '../../store/useTeamStore';
import { useScenarioStore } from '../../store/useScenarioStore';
import { useUiStore } from '../../store/useUiStore';
import { SPIRIT_MAP } from '../../data/mock-spirits';
import { SKILL_MAP } from '../../data/mock-skills';
import { NATURE_MAP } from '../../data/mock-natures';
import {
  calculateStats,
  makeDefaultEvs,
  makeDefaultIvs,
  EV_MAX,
} from '../../core/stats';
import type { StatKey, TeamSlot } from '../../core/types';

// 复制自damage.ts，每级±10%
function stageMultiplier(stage: number): number {
  const s = Math.max(-20, Math.min(20, stage));
  return s >= 0 ? 1 + s * 0.1 : 1 - Math.abs(s) * 0.1;
}
function applyBoost(base: number, stage = 0): number {
  return Math.round(base * stageMultiplier(stage));
}

const STAT_KEYS: StatKey[] = ['hp', 'atk', 'spa', 'def', 'spd', 'spe'];
const STAT_LABELS: Record<StatKey, string> = {
  hp: '生命',
  atk: '物攻',
  spa: '魔攻',
  def: '物防',
  spd: '魔防',
  spe: '速度',
};

export function TeamPanel() {
  // ✅所有 Hook 必须写在组件函数体最顶部
  const team = useTeamStore((s) => s.team);
  const attackerIndex = useScenarioStore((s) => s.attackerIndex);
  const defenderIndex = useScenarioStore((s) => s.defenderIndex);
  const swapSides = useScenarioStore((s) => s.swapSides);
  const openSpiritPicker = useUiStore((s) => s.openSpiritPicker);
  const openSkillPicker = useUiStore((s) => s.openSkillPicker);
  const openNatureEditor = useUiStore((s) => s.openNatureEditor);
  const addSkillResult = useScenarioStore((s) => s.addSkillResult);
  // ✅把原来第31行的 skillResults 移到这里
  const skillResults = useScenarioStore((s) => s.skillResults);

  const attackerSlot = team.slots[attackerIndex];
  const defenderSlot = team.slots[defenderIndex];

  // 配置页独立的自定义技能状态
  const [teamCustomPower, setTeamCustomPower] = useState<number>(100);
  const [teamCustomCategory, setTeamCustomCategory] = useState<'physical' | 'special'>('physical');
  const [teamCustomType, setTeamCustomType] = useState<string>('普通');

  function teamPanelCalcCustomSkill() {
    if (!attackerSlot?.spiritId || !defenderSlot?.spiritId) return;
    const attackerSpirit = SPIRIT_MAP[attackerSlot.spiritId];
    const defenderSpirit = SPIRIT_MAP[defenderSlot.spiritId];
    if (!attackerSpirit || !defenderSpirit) return;

    const attackerStats = calculateStats(
      attackerSlot.customBaseStats ?? attackerSpirit.baseStats,
      attackerSlot.ivs,
      attackerSlot.evs,
      NATURE_MAP[attackerSlot.nature],
      attackerSlot.level
    );
    const defenderStats = calculateStats(
      defenderSlot.customBaseStats ?? defenderSpirit.baseStats,
      defenderSlot.ivs,
      defenderSlot.evs,
      NATURE_MAP[defenderSlot.nature],
      defenderSlot.level
    );

    const tempSkill: Skill = {
      id: 'custom-skill',
      name: '自定义技能',
      type: teamCustomType,
      power: teamCustomPower,
      category: teamCustomCategory,
      accuracy: 100,
    };

    const damageInput = {
      attacker: {
        spirit: attackerSpirit,
        stats: attackerStats,
        boosts: attackerSlot.boosts,
        skill: tempSkill,
      },
      defender: {
        spirit: defenderSpirit,
        stats: defenderStats,
        boosts: defenderSlot.boosts,
      },
      field: { crit: false, weather: 'none' as const },
    };

    const res: DamageResult = calculateDamage(damageInput);

    const item: SkillResultItem = {
      skillId: 'custom-skill',
      skillName: `自定义${teamCustomType} ${teamCustomCategory === 'physical' ? '物理' : '魔法'}(威力${teamCustomPower})`,
      result: res
    };
    addSkillResult(item);
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SlotCard
            label="攻击方"
            color="text-red-400"
            slotIndex={attackerIndex}
            slot={attackerSlot}
            isAttacker={true}
            onPickSpirit={() => openSpiritPicker(attackerIndex)}
            onPickSkills={() => openSkillPicker(attackerIndex)}
            onEditNature={() => openNatureEditor(attackerIndex)}
          />
          <SlotCard
            label="防守方"
            color="text-blue-400"
            slotIndex={defenderIndex}
            slot={defenderSlot}
            isAttacker={false}
            onPickSpirit={() => openSpiritPicker(defenderIndex)}
            onPickSkills={() => openSkillPicker(defenderIndex)}
            onEditNature={() => openNatureEditor(defenderIndex)}
          />
        </div>
        <button
          onClick={swapSides}
          title="切换攻守"
          className="hidden sm:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full items-center justify-center bg-slate-800 border border-slate-600 text-slate-300 shadow-lg hover:bg-primary-600 hover:border-primary-500 hover:text-white hover:rotate-180 transition-all duration-300"
        >
          <ArrowLeftRight size={18} />
        </button>
      </div>
      <div className="sm:hidden flex justify-center mt-3">
        <button
          onClick={swapSides}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:border-primary-500 hover:text-white transition"
        >
          <ArrowLeftRight size={14} />
          切换攻守
        </button>
      </div>

      <p className="mt-6 text-xs text-slate-500 text-center">
        提示：点击数值卡片可一键加满个体值（再次点击取消）
      </p>

      {/* 配置页 - 自定义技能卡片 */}
      <div className="mt-6 p-4 rounded-xl border border-slate-600 bg-slate-800/80">
        <h3 className="text-sm font-semibold mb-3 text-slate-100">自定义技能</h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-slate-200 text-sm">威力:</span>
            <input
              type="number"
              min={0}
              value={teamCustomPower}
              onChange={(e) => setTeamCustomPower(Number(e.target.value || 0))}
              className="w-20 bg-slate-700 border border-slate-500 rounded px-2 py-1 text-white font-semibold placeholder-slate-400 focus:outline-none focus:border-blue-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-200 text-sm">伤害类型:</span>
            <select
              value={teamCustomCategory}
              onChange={(e) => setTeamCustomCategory(e.target.value as 'physical' | 'special')}
              className="bg-slate-700 border border-slate-500 rounded px-2 py-1 text-white font-semibold focus:outline-none focus:border-blue-400"
            >
              <option value="physical">物理</option>
              <option value="special">魔法</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-200 text-sm">系别:</span>
            <select
              value={teamCustomType}
              onChange={(e) => setTeamCustomType(e.target.value)}
              className="bg-slate-700 border border-slate-500 rounded px-2 py-1 text-white font-semibold focus:outline-none focus:border-blue-400"
            >
              <option value="普通">普通</option>
              <option value="火">火</option>
              <option value="水">水</option>
              <option value="草">草</option>
              <option value="电">电</option>
              <option value="冰">冰</option>
              <option value="武">武</option>
              <option value="毒">毒</option>
              <option value="土">土</option>
              <option value="翼">翼</option>
              <option value="萌">萌</option>
              <option value="虫">虫</option>
              <option value="石">石</option>
              <option value="幽">幽</option>
              <option value="龙">龙</option>
              <option value="恶">恶</option>
              <option value="光">光</option>
            </select>
          </div>
          <button
            onClick={teamPanelCalcCustomSkill}
            className="bg-blue-600 hover:bg-blue-500 active:bg-blue-700 px-4 py-1.5 rounded text-white text-sm font-semibold shadow"
          >
            计算自定义技能
          </button>
        </div>
      </div>

      {/* 多技能结果对比面板（新增） */}
      <div className="mt-6 p-4 rounded-xl border border-slate-700 bg-slate-800">
        <h3 className="text-sm font-semibold mb-3 text-slate-300">计算结果</h3>
        {skillResults.length > 0 ? (
          <div className="space-y-4">
            {skillResults.map((item) => (
              <div key={item.skillId} className="border border-slate-700 rounded-lg p-3 bg-slate-900/40">
                <div className="text-sm text-cyan-300 mb-2">{item.skillName}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <StatBlock label="最小伤害" value={item.result.min} />
                  <StatBlock label="最大伤害" value={item.result.max} />
                  <StatBlock label="平均伤害" value={item.result.avg} />
                  <StatBlock
                    label="伤害占比"
                    value={`${item.result.percentMin.toFixed(1)}% ~ ${item.result.percentMax.toFixed(1)}%`}
                  />
                  <StatBlock
                    label="击杀概率"
                    value={`${(item.result.koChance * 100).toFixed(1)}%`}
                    highlight={item.result.koChance >= 1}
                  />
                  <StatBlock label="剩余 HP" value={`${item.result.remainingHp}`} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-sm">点击技能或者计算自定义技能查看伤害</p>
        )}
      </div>
    </div>
  );
}

function SlotCard({
  label,
  color,
  slotIndex,
  slot,
  isAttacker,
  onPickSpirit,
  onPickSkills,
  onEditNature,
}: {
  label: string;
  color: string;
  slotIndex: number;
  slot: TeamSlot | undefined;
  isAttacker: boolean;
  onPickSpirit: () => void;
  onPickSkills: () => void;
  onEditNature: () => void;
}) {
  const setSlotIv = useTeamStore((s) => s.setSlotIv);
  const setSlotBoost = useTeamStore((s) => s.setSlotBoost);

  const spirit = slot?.spiritId ? SPIRIT_MAP[slot.spiritId] : null;
  const baseStats = slot && spirit ? slot.customBaseStats ?? spirit.baseStats : null;
  const ivs = slot ? slot.ivs ?? makeDefaultIvs() : null;
  const boosts = slot?.boosts ?? { atk: 0, spa: 0, def: 0, spd: 0 };

  const finalStatsNoBoost =
    spirit && slot && baseStats && ivs
      ? calculateStats(
          baseStats,
          ivs,
          makeDefaultEvs(),
          NATURE_MAP[slot.nature],
          slot.level ?? 60,
        )
      : null;

  const natureName = slot ? NATURE_MAP[slot.nature]?.name ?? '—' : '—';
  const skills = slot?.skills.map((id) => SKILL_MAP[id]?.name).filter(Boolean) ?? [];

  const currentBoostLv = isAttacker ? boosts.atk : boosts.def;

  const changeLv = (delta: number) => {
    setSlotBoost(slotIndex, isAttacker ? 'attack' : 'defense', currentBoostLv + delta);
  };
  const handleInputChange = (val: string) => {
    const num = parseInt(val, 10);
    if (!Number.isNaN(num)) {
      setSlotBoost(slotIndex, isAttacker ? 'attack' : 'defense', num);
    }
  };

  return (
    <div className="p-4 rounded-xl border border-slate-700 bg-slate-800 space-y-3">
      <p className={`text-xs ${color}`}>{label}</p>
      <button
        onClick={onPickSpirit}
        className="w-full text-left px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 hover:border-slate-500 transition"
      >
        <p className="text-white font-semibold truncate">{spirit?.name ?? '点击选择'}</p>
      </button>
      <button
        onClick={onEditNature}
        className="w-full text-left px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 hover:border-slate-500 transition flex items-center justify-between"
      >
        <span className="text-xs text-slate-400">性格 / 等级</span>
        <span className="text-xs text-slate-200 font-mono">
          {natureName} · Lv.{slot?.level ?? 60}
        </span>
      </button>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-400">技能</span>
          <button onClick={onPickSkills} className="text-xs text-primary-400 hover:text-primary-300">
            编辑
          </button>
        </div>
        {skills.length ? (
          <ul className="text-xs text-slate-300 space-y-0.5">
            {skills.map((name, i) => (
              <li key={i} className="truncate">· {name}</li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-slate-500">未配置</p>
        )}
      </div>

      {finalStatsNoBoost && baseStats && ivs ? (
        <>
          <div className="w-full grid grid-cols-3 gap-2">
            {STAT_KEYS.map((key) => {
              const isBoosted = ivs[key] >= EV_MAX;
              const baseVal = finalStatsNoBoost[key];
              let displayVal = baseVal;
              let diff = 0;
              if (key === 'atk') {
                displayVal = applyBoost(baseVal, boosts.atk);
                diff = displayVal - baseVal;
              } else if (key === 'spa') {
                displayVal = applyBoost(baseVal, boosts.spa);
                diff = displayVal - baseVal;
              } else if (key === 'def') {
                displayVal = applyBoost(baseVal, boosts.def);
                diff = displayVal - baseVal;
              } else if (key === 'spd') {
                displayVal = applyBoost(baseVal, boosts.spd);
                diff = displayVal - baseVal;
              }

              return (
                <button
                  key={key}
                  onClick={() => setSlotIv(slotIndex, key, isBoosted ? 0 : EV_MAX)}
                  title={isBoosted ? '点击取消满个体' : '点击加满个体值'}
                  className={`rounded-lg bg-slate-900 p-2 text-left transition flex items-center justify-between gap-1 ${
                    isBoosted
                      ? 'border-2 border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.25)]'
                      : 'border border-slate-700 hover:border-slate-500'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-[10px] font-medium ${isBoosted ? 'text-blue-300' : 'text-slate-500'}`}>
                      {STAT_LABELS[key]}
                    </div>
                    <div className="text-lg text-red-400 font-mono font-bold leading-tight flex items-center gap-1">
                      {displayVal}
                      {diff !== 0 && (
                        <span className={`text-xs ${diff > 0 ? 'text-green-400' : 'text-orange-400'}`}>
                          {diff > 0 ? `+${diff}` : diff}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">种族{baseStats[key]}</div>
                  </div>
                  <div className="shrink-0 flex flex-col items-center">
                    <div className={`text-[10px] ${isBoosted ? 'text-blue-300' : 'text-slate-400'}`}>个体</div>
                    <div
                      className={`text-xs font-mono rounded px-1.5 py-0.5 min-w-[28px] text-center ${
                        isBoosted
                          ? 'bg-blue-500/20 border border-blue-500/60 text-white'
                          : 'bg-slate-800 border border-slate-700 text-white'
                      }`}
                    >
                      {ivs[key]}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between bg-slate-900 rounded-lg border border-slate-700 px-3 py-2">
            <span className="text-xs text-slate-300">{isAttacker ? '攻击等级' : '防御等级'}</span>
            <div className="flex gap-1 items-center">
              <button
                onClick={() => changeLv(-1)}
                className="w-7 h-7 rounded bg-slate-800 border border-slate-600 hover:bg-slate-700 text-white text-sm flex items-center justify-center"
              >
                -
              </button>
              <input
                type="number"
                min={-20}
                max={20}
                value={currentBoostLv}
                onChange={(e) => {
                  const val = e.target.value;
                  const num = parseInt(val, 10);
                  const safeNum = Number.isNaN(num) ? 0 : num;
                  setSlotBoost(slotIndex, isAttacker ? 'attack' : 'defense', safeNum);
                }}
                className="w-14 h-6 bg-slate-800 border border-slate-600 rounded text-center text-sm text-white leading-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                onClick={() => changeLv(+1)}
                className="w-7 h-7 rounded bg-slate-800 border border-slate-600 hover:bg-slate-700 text-white text-sm flex items-center justify-center"
              >
                +
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {STAT_KEYS.map((key) => (
            <div key={key} className="rounded-lg bg-slate-900 border border-slate-700 p-2 opacity-40">
              <div className="text-[10px] text-slate-500 font-medium">{STAT_LABELS[key]}</div>
              <div className="text-lg text-slate-600 font-mono font-bold leading-tight">—</div>
              <div className="text-[10px] text-slate-600 font-mono">种族:—</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
